// SSE utility
// This will provide core functionality that components can use

import { nanoid } from 'nanoid'
let isConnected = false
let sessionId = ''
let clientId = '' // Add client ID state variable
let eventSource: EventSource | null = null
let eventListeners: Array<(event: any) => void> = []
let toolEvents: Array<{ type: string; message?: string; data?: any }> = []

// Reconnect backoff state
let reconnectTimer: number | null = null
let backoffMs = 1000
const MAX_BACKOFF_MS = 30000

// Generate a session ID
export function generateSessionId(username?: string): string {
  if (username) {
    return `${username}-${nanoid()}-${Date.now()}`
  }
  return `${nanoid()}-${Date.now()}`
}

// Connect to SSE endpoint
export function connectSSE(id?: string, onEvent?: (event: any) => void): void {
  // Close existing connection if any to ensure a fresh start.
  if (eventSource) {
    disconnectSSE()
  }

  // Generate or use provided session ID
  if (id) {
    sessionId = id
  } else if (!sessionId) {
    sessionId = generateSessionId()
  }

  console.log(`Connecting to SSE with session ID: ${sessionId}`)

  // Create new EventSource connection
  eventSource = new EventSource(`/v1/events/subscribe?sessionId=${sessionId}`)

  eventSource.onopen = () => {
    console.log('SSE connection opened')
    isConnected = true
    backoffMs = 1000
    const event = { type: 'connected', message: 'Connection established' }
    addEvent(event)
    eventListeners.forEach((listener) => listener(event))
    if (onEvent) onEvent(event)
  }

  eventSource.onerror = (error) => {
    console.error('SSE connection error:', error)
    const event = { type: 'error', message: 'Connection error' }
    addEvent(event)
    eventListeners.forEach((listener) => listener(event))

    // Mark disconnected and clear current client binding
    isConnected = false
    try { eventSource?.close() } catch (_err) { /* ignore */ }
    eventSource = null
    clientId = ''

    // Exponential backoff reconnect preserving sessionId
    if (reconnectTimer == null) {
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null
        connectSSE(sessionId, onEvent)
        backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS)
      }, backoffMs)
    }
  }

  eventSource.onmessage = (e) => {
    try {
      const event = JSON.parse(e.data)
      console.log('SSE event received:', event)

      // Process tool_end events with potential JSON content
      if (event.type === 'tool_end' && event.data?.result?.content) {
        try {
          const content = event.data.result.content;
          if (Array.isArray(content)) {
            for (let i = 0; i < content.length; i++) {
              const item = content[i];
              // Only attempt to parse if it looks like a JSON object/array string
              if (item.type === 'text' && typeof item.text === 'string' && item.text.trim().startsWith('{') && item.text.trim().endsWith('}')) {
                try {
                  item.text = JSON.parse(item.text);
                } catch (parseError) {
                  // It looked like JSON but wasn't. Ignore the error and keep the original text.
                }
              }
            }
            event.data.result.content = content;
          }
        } catch (error) {
          console.error('Error processing tool_end event content:', error);
        }
      }

      // Check if this event contains a client ID
      if (event.type === 'connected' && event.clientId) {
        clientId = event.clientId
        console.log(`Client ID received: ${clientId}`)
      }

      addEvent(event)

      // Notify all registered listeners
      eventListeners.forEach((listener) => listener(event))
      if (onEvent) {
        onEvent(event)
      }
    } catch (error) {
      console.error('Error parsing SSE event:', error)
      const errorEvent = { type: 'error', message: 'Failed to parse event data', data: e.data }
      addEvent(errorEvent)
      eventListeners.forEach((listener) => listener(errorEvent))
    }
  }
}

// Disconnect from SSE
export function disconnectSSE(): void {
  if (eventSource) {
    console.log('Disconnecting from SSE')
    eventSource.close()
    eventSource = null
  }
  if (reconnectTimer != null) {
    window.clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  isConnected = false
  clientId = '' // Reset client ID when disconnecting
  const event = { type: 'system', message: 'Connection closed' }
  addEvent(event)
  eventListeners.forEach((listener) => listener(event))
  clearEvents() // Clear events on disconnect
}

// Add an event to the toolEvents array
export function addEvent(event: { type: string; message?: string; data?: any }): void {
  toolEvents.push(event)

  // Limit the number of events to prevent memory issues
  if (toolEvents.length > 100) {
    toolEvents.shift()
  }
}

// Clear all events
export function clearEvents(): void {
  toolEvents.splice(0, toolEvents.length)
}

// Register an event listener
export function addEventListener(listener: (event: any) => void): void {
  eventListeners.push(listener)
}

// Remove an event listener
export function removeEventListener(listener: (event: any) => void): void {
  const index = eventListeners.indexOf(listener)
  if (index !== -1) {
    eventListeners.splice(index, 1)
  }
}

// Update the session ID without reconnecting
export function updateSessionId(newSessionId: string): void {
  sessionId = newSessionId
  console.log(`Updated session ID: ${sessionId}`)
}

// Get current state (allows components to access current state without reactivity)
export function getState() {
  return {
    isConnected,
    sessionId,
    clientId, // Include client ID in the state
    events: [...toolEvents] // Return a copy to prevent outside modification
  }
}
